import captureError from '@disco/utils/captureError'
import { loadBusinessData } from '@disco/state-management/actions/UserActions'
import api from '@disco/api/server'
import {
  DISCOVERY_SUITE,
  STORAGE,
  USER,
  WATERMARKING,
} from '@disco/constants/billing-provider-components'
import { getChargebeeAddon } from '@disco/utils/chargebee'

type ItemToAdd = {
  newValue: number
  component_id?: number
  type:
    | typeof DISCOVERY_SUITE
    | typeof WATERMARKING
    | typeof STORAGE
    | typeof USER
}

type SeatsAndTracksUpdateArgs = {
  items: ItemToAdd[]
  coupon_code?: string
}

/**
 * Updates seats and tracks allocation for a business subscription with retry logic.
 * Handles component updates (users, storage, watermarking, discovery suite) and billing.
 * @param params - Update parameters object
 * @param params.items - Array of component updates with new values and component IDs
 * @param params.coupon_code - Optional coupon code to apply during update
 * @returns Thunk that updates allocations with retry mechanism and business data refresh
 */
export const seatsAndTracksUpdate = ({
  items,
  coupon_code,
}: SeatsAndTracksUpdateArgs): ((
  dispatch: any,
  getState: any,
) => Promise<void>) => {
  return async (dispatch: (...args: Array<any>) => any) => {
    const allocateAddonForChargebee = async () => {
      const allAddOns = await api.chargebeeListAddons()
      const flatAllAddOns = items.flatMap(({ type, newValue }) =>
        getChargebeeAddon({
          type,
          allocated_quantity: newValue,
          allAddons: allAddOns,
        }),
      )
      const orderForm = { addons: flatAllAddOns, coupon: coupon_code ?? '' }
      await api.allocateAddons(orderForm)
      await dispatch(loadBusinessData())
    }

    try {
      await allocateAddonForChargebee()
    } catch (error) {
      captureError(error, { description: 'Error to update seats or tracks' })
      throw new Error(error)
    }
  }
}
